import { availableLaborCost } from "./laborMetricCost";
import LaborMetricPair from "./LaborMetricPair";
import { SANDBOX_IMPACT_TOOLTIPS } from "./laborModelTooltipCopy";
import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  CompanyStoreSelector,
  LaborPlanComparisonChart,
  StoreTrendSparkline,
} from "./LocationDetailsLaborPlanTable";
import { LaborModelStepBadge } from "./LaborModelSection";
import {
  getPublicationDisplayName,
  getPublicationStatus,
} from "./laborModelPublicationStatus";

const SUMMARY_EXPANDED_STORAGE_KEY = "labor-model-plan-summary-expanded";

function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function addCosts(total, cost) {
  return total === null || cost === null ? null : total + cost;
}

function hourlyCost(group) {
  const cost = availableLaborCost(group?.hours, group?.labor_cost);
  if (cost === null) return null;
  const hours = numberValue(group?.hours);
  return hours > 0 ? cost / hours : cost === 0 ? 0 : null;
}

function costForHours(hours, rate) {
  return rate === null ? null : numberValue(hours) * rate;
}

function formatNumber(value, digits = 1) {
  return numberValue(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
}

function formatCurrency(value, digits = 0) {
  return numberValue(value).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
  });
}

function formatPercent(value) {
  return `${formatNumber(value, 1)}%`;
}

function formatPrimaryMetric(metricGroup, metricKey) {
  if (metricKey === "laborPercent") {
    return Number.isFinite(metricGroup?.laborPercent) ? formatPercent(metricGroup.laborPercent) : "—";
  }
  return formatCurrency(metricGroup?.splh, 2);
}

function getPrimaryMetricLabel(metricKey) {
  return metricKey === "laborPercent" ? "Labor %" : "SPLH";
}

function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return "Next business week";
  const format = (value) =>
    new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  return startDate === endDate
    ? format(startDate)
    : `${format(startDate)}–${format(endDate)}`;
}

export function getMetricGroup(hours, cost, sales) {
  const pricedCost = availableLaborCost(hours, cost);
  return {
    hours,
    cost: pricedCost,
    sales,
    laborPercent: sales > 0 && pricedCost !== null ? (pricedCost / sales) * 100 : null,
    splh: hours > 0 ? sales / hours : null,
  };
}

function aggregateEvaluation(testResults, selectedStoreIds) {
  const selectedSet = new Set((selectedStoreIds || []).map(String));
  const totals = {
    targetHours: 0,
    targetCost: 0,
    comparisonHours: 0,
    comparisonCost: 0,
    sales: 0,
  };
  const daily = new Map();
  const stores = [];

  Object.entries(testResults || {}).forEach(([storeName, days]) => {
    const firstDay = (days || [])[0];
    const storeId = String(firstDay?.store_id || storeName);
    if (selectedSet.size > 0 && !selectedSet.has(storeId)) return;

    const storeTotals = {
      targetHours: 0,
      targetCost: 0,
      comparisonHours: 0,
      comparisonCost: 0,
      sales: 0,
    };

    (days || []).forEach((day) => {
      const dayKey = day.date || day.day_name;
      const dayTotals = daily.get(dayKey) || {
        targetHours: 0,
        targetCost: 0,
        comparisonHours: 0,
        comparisonCost: 0,
        sales: 0,
      };
      const sales = numberValue(day.sales);
      storeTotals.sales += sales;
      dayTotals.sales += sales;

      (day.labor_results || []).forEach((result) => {
        const targetHours = numberValue(result.new_hours);
        const targetCost = availableLaborCost(targetHours, result.new_cost);
        const comparisonHours = numberValue(result.old_hours);
        const comparisonCost = availableLaborCost(comparisonHours, result.old_cost);
        storeTotals.targetHours += targetHours;
        storeTotals.targetCost = addCosts(storeTotals.targetCost, targetCost);
        storeTotals.comparisonHours += comparisonHours;
        storeTotals.comparisonCost = addCosts(storeTotals.comparisonCost, comparisonCost);
        dayTotals.targetHours += targetHours;
        dayTotals.targetCost = addCosts(dayTotals.targetCost, targetCost);
        dayTotals.comparisonHours += comparisonHours;
        dayTotals.comparisonCost = addCosts(dayTotals.comparisonCost, comparisonCost);
      });
      daily.set(dayKey, dayTotals);
    });

    Object.keys(totals).forEach((key) => {
      totals[key] = key.endsWith("Cost")
        ? addCosts(totals[key], storeTotals[key])
        : totals[key] + storeTotals[key];
    });
    stores.push({
      storeId,
      storeName,
      target: getMetricGroup(
        storeTotals.targetHours,
        storeTotals.targetCost,
        storeTotals.sales
      ),
      comparison: getMetricGroup(
        storeTotals.comparisonHours,
        storeTotals.comparisonCost,
        storeTotals.sales
      ),
    });
  });

  return {
    target: getMetricGroup(totals.targetHours, totals.targetCost, totals.sales),
    comparison: getMetricGroup(
      totals.comparisonHours,
      totals.comparisonCost,
      totals.sales
    ),
    daily,
    stores,
  };
}

function buildPublishedEvaluation(laborPlanSummary, stores, weeksBack, weeksForward) {
  if (!laborPlanSummary?.store_series) return null;

  const storeNames = Object.fromEntries(
    (stores || []).map((store) => [
      String(store.id),
      store.store_name || store.storeName || store.name || `Store ${store.id}`,
    ])
  );
  const storeSummaries = Object.fromEntries(
    (laborPlanSummary.stores || []).map((store) => [String(store.store_id), store])
  );
  const historyRows = [];
  const forecastRows = [];

  Object.values(laborPlanSummary.store_series).forEach((rows) => {
    (rows || []).forEach((row) => {
      (row.period === "historical" ? historyRows : forecastRows).push(row.date);
    });
  });
  const selectedDates = (dates, count, fromEnd = false) => {
    const uniqueDates = [...new Set(dates)].sort();
    return new Set(
      fromEnd ? uniqueDates.slice(-count * 7) : uniqueDates.slice(0, count * 7)
    );
  };
  const selectedHistoryDates = selectedDates(historyRows, weeksBack, true);
  const selectedForecastDates = selectedDates(forecastRows, weeksForward);
  const historyResults = {};
  const forecastResults = {};

  Object.entries(laborPlanSummary.store_series).forEach(([storeId, rows]) => {
    const storeSummary = storeSummaries[String(storeId)] || {};
    const actualRate = hourlyCost(storeSummary.actual);
    const earnedRate = hourlyCost(storeSummary.earned);
    const plannedRate = hourlyCost(storeSummary.planned);
    const storeName = storeNames[String(storeId)] || `Store ${storeId}`;
    historyResults[storeName] = (rows || [])
      .filter((row) => row.period === "historical" && selectedHistoryDates.has(row.date))
      .map((row) => ({
        store_id: storeId,
        date: row.date,
        sales: numberValue(row.actual?.sales),
        labor_results: [
          {
            new_hours: numberValue(row.earned?.hours),
            new_cost: costForHours(row.earned?.hours, earnedRate),
            old_hours: numberValue(row.actual?.hours),
            old_cost: costForHours(row.actual?.hours, actualRate),
          },
        ],
      }));
    forecastResults[storeName] = (rows || [])
      .filter((row) => row.period === "forecast" && selectedForecastDates.has(row.date))
      .map((row) => ({
        store_id: storeId,
        date: row.date,
        sales: numberValue(row.planned?.sales ?? row.forecasted?.sales),
        labor_results: [
          {
            new_hours: numberValue(row.planned?.hours ?? row.forecasted?.hours),
            new_cost:
              costForHours(row.planned?.hours ?? row.forecasted?.hours, plannedRate),
            old_hours: numberValue(row.planned?.hours ?? row.forecasted?.hours),
            old_cost:
              costForHours(row.planned?.hours ?? row.forecasted?.hours, plannedRate),
          },
        ],
      }));
  });

  const dateRange = (dates) => {
    const values = [...dates].sort();
    return { dateStart: values[0] || null, dateEnd: values[values.length - 1] || null };
  };
  return {
    laborPlanSummary,
    history: {
      ...dateRange(selectedHistoryDates),
      testResults: historyResults,
      dailyTestResults: historyResults,
    },
    forecast: {
      ...dateRange(selectedForecastDates),
      testResults: forecastResults,
      dailyTestResults: forecastResults,
    },
  };
}

function percentDelta(value, comparisonValue) {
  return comparisonValue
    ? ((value - comparisonValue) / comparisonValue) * 100
    : null;
}

function formatDelta(value, suffix = "%") {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value > 0 ? "+" : ""}${formatNumber(value, 1)}${suffix}`;
}

function LaborHoursDeltaValue({ value, suffixClassName = "text-slate-400" }) {
  return (
    <>
      {formatDelta(value)}
      {Number.isFinite(value) ? (
        <span className={`ml-1 text-[10px] font-medium ${suffixClassName}`}>
          of hrs
        </span>
      ) : null}
    </>
  );
}

function getLaborHoursPercentComparison(targetHours, comparisonHours, label) {
  const deltaPercent = percentDelta(targetHours, comparisonHours);
  if (deltaPercent === null) {
    return null;
  }

  if (Math.abs(deltaPercent) < 0.05) {
    return {
      label,
      direction: "the same number of",
      value: null,
      tone: "text-slate-700",
    };
  }

  return {
    label,
    direction: deltaPercent > 0 ? "more" : "fewer",
    value: formatPercent(Math.abs(deltaPercent)),
    tone: deltaPercent > 0 ? "text-rose-600" : "text-emerald-600",
  };
}

function LaborHoursComparisonStatement({
  comparisons,
  compact = false,
  subject = "This sandbox",
}) {
  const visibleComparisons = (comparisons || []).filter(Boolean);
  const className = compact
    ? "mt-2 text-xs font-medium leading-5 text-slate-600"
    : "mt-3 text-sm font-medium leading-5 text-slate-600";
  const mutedClassName = compact
    ? "mt-2 text-xs font-medium leading-5 text-slate-500"
    : "mt-3 text-sm font-medium leading-5 text-slate-500";
  if (!visibleComparisons.length) {
    return (
      <p className={mutedClassName}>
        Not enough data to compare labor hours.
      </p>
    );
  }

  return (
    <p className={className}>
      {subject} recommends{" "}
      {visibleComparisons.map((comparison, index) => (
        <React.Fragment key={comparison.label}>
          {index > 0 ? " and " : null}
          {comparison.value ? (
            <span className={`font-semibold ${comparison.tone}`}>
              {comparison.value}
            </span>
          ) : null}
          {comparison.value ? (
            <>
              {" "}
              {comparison.direction} hours than {comparison.label}
            </>
          ) : (
            <>the same number of hours as {comparison.label}</>
          )}
        </React.Fragment>
      ))}
      .
    </p>
  );
}

function SummaryLaborMetrics({ value, primaryMetricLabel, metrics, laborPercentAvailable, compact }) {
  const secondaryKey = primaryMetricLabel === "Labor %" ? "splh" : "laborPercent";
  const items = [{ label: primaryMetricLabel, value }];
  if (laborPercentAvailable) {
    items.push({
      label: getPrimaryMetricLabel(secondaryKey),
      value: metrics?.sales > 0 && Number.isFinite(metrics?.[secondaryKey])
        ? formatPrimaryMetric(metrics, secondaryKey)
        : "—",
    });
  }
  return <LaborMetricPair items={items} compact={compact} />;
}

function ImpactSummaryCard({
  eyebrow,
  dateRange,
  value,
  primaryMetricLabel,
  metrics,
  laborPercentAvailable,
  comparisonStatements,
  comparisonSubject,
  compact = false,
}) {
  return (
    <div
      className={`min-w-0 rounded-xl border border-slate-200 bg-slate-50/50 ${
        compact ? "p-3" : "p-4"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {eyebrow}
        </p>
        <p className="text-xs font-medium text-slate-500">{dateRange}</p>
      </div>
      <SummaryLaborMetrics
        value={value}
        primaryMetricLabel={primaryMetricLabel}
        metrics={metrics}
        laborPercentAvailable={laborPercentAvailable}
      />
      {comparisonStatements !== null ? (
        <LaborHoursComparisonStatement
          comparisons={comparisonStatements}
          compact={compact}
          subject={comparisonSubject}
        />
      ) : null}
    </div>
  );
}

function LaborMetricTooltipContent({ label, scope, showImpact, laborPercentAvailable }) {
  return (
    <div className="space-y-2 text-xs leading-5">
      <p className="font-semibold">{label} labor</p>
      <p>{scope}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-semibold">SPLH</dt>
        <dd>Sales per labor hour.</dd>
        {laborPercentAvailable ? (
          <>
            <dt className="font-semibold">Labor %</dt>
            <dd>Labor cost as a percentage of sales.</dd>
          </>
        ) : null}
      </dl>
      {showImpact ? <p>{SANDBOX_IMPACT_TOOLTIPS[label]}</p> : (
        <p>{label === "Earned" ? "Based on actual sales in the historical period." : "Based on forecast sales in the forecast period."}</p>
      )}
    </div>
  );
}

const METRIC_TOOLTIP_SLOT_PROPS = {
  tooltip: { sx: { maxWidth: 360, padding: "12px 16px" } },
};

function CompactLaborReviewMetrics({ earned, forecasted, earnedDelta, forecastedDelta, laborPercentAvailable, showImpact = true, scope }) {
  return (
    <table className="text-xs" aria-label={showImpact ? "Sandbox labor metrics and hours change versus published" : "Published labor metrics"}>
      <tbody>
        {[["Earned", earned, earnedDelta], ["Forecasted", forecasted, forecastedDelta]].map(([label, metrics, delta]) => (
          <Tooltip key={label} title={<LaborMetricTooltipContent label={label} scope={scope} showImpact={showImpact} laborPercentAvailable={laborPercentAvailable} />} slotProps={METRIC_TOOLTIP_SLOT_PROPS} arrow describeChild enterTouchDelay={0}>
            <tr tabIndex={0} className="cursor-help">
              <th scope="row" className="py-1 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</th>
              <td className="whitespace-nowrap py-1 pr-3 text-right" role="group" aria-label={`${label} SPLH: ${metrics?.splh > 0 ? formatCurrency(metrics.splh, 2) : "—"}`}>
                <span className="font-semibold tabular-nums text-slate-900">{metrics?.splh > 0 ? formatCurrency(metrics.splh, 2) : "—"}</span>{" "}
                <span className="text-[10px] text-slate-500">SPLH</span>
              </td>
              {laborPercentAvailable ? (
                <td className="whitespace-nowrap py-1 pr-3 text-right" role="group" aria-label={`${label} Labor %: ${metrics?.sales > 0 && Number.isFinite(metrics?.laborPercent) ? formatPercent(metrics.laborPercent) : "—"}`}>
                  <span className="font-semibold tabular-nums text-slate-900">{metrics?.sales > 0 && Number.isFinite(metrics?.laborPercent) ? formatPercent(metrics.laborPercent) : "—"}</span>{" "}
                  <span className="text-[10px] text-slate-500">Labor %</span>
                </td>
              ) : null}
              {showImpact ? (
              <td className="whitespace-nowrap py-1 text-right">
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${!Number.isFinite(delta) || Math.abs(delta) < 0.05 ? "bg-slate-100 text-slate-600" : delta > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                  {Number.isFinite(delta) ? formatDelta(delta) : "—"} hours
                </span>
              </td>
              ) : null}
            </tr>
          </Tooltip>
        ))}
      </tbody>
    </table>
  );
}

export function LaborSummaryBadge({ label, value, metrics, laborPercentAvailable, showImpact = false, delta, scope }) {
  const formattedValue = numberValue(value) > 0 ? formatCurrency(value, 2) : "—";
  const laborPercent = metrics?.sales > 0 && Number.isFinite(metrics?.laborPercent)
    ? formatPercent(metrics.laborPercent)
    : "—";
  const deltaTone = !Number.isFinite(delta) || Math.abs(delta) < 0.05
    ? "bg-slate-100 text-slate-600"
    : delta > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700";
  return (
    <Tooltip title={<LaborMetricTooltipContent label={label} scope={scope} showImpact={showImpact} laborPercentAvailable={laborPercentAvailable} />} slotProps={METRIC_TOOLTIP_SLOT_PROPS} arrow describeChild enterTouchDelay={0}>
    <div tabIndex={0} className="cursor-help rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex items-center gap-3">
        <div role="group" aria-label={`${label} SPLH: ${formattedValue}`} className="flex items-baseline gap-1.5">
          <span className="text-sm font-semibold tabular-nums text-slate-900">{formattedValue}</span>
          <span className="text-[11px] text-slate-500">SPLH</span>
        </div>
        {laborPercentAvailable ? (
          <div className="flex items-baseline gap-1.5 border-l border-slate-200 pl-3" role="group" aria-label={`${label} Labor %: ${laborPercent}`}>
            <span className="text-sm font-semibold tabular-nums text-slate-900">{laborPercent}</span>
            <span className="whitespace-nowrap text-[11px] text-slate-500">Labor %</span>
          </div>
        ) : null}
      </div>
      {showImpact ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-slate-200 pt-2 text-[11px]">
          <span className={`rounded px-1.5 py-0.5 font-semibold tabular-nums ${deltaTone}`}>
            {Number.isFinite(delta) ? formatDelta(delta) : "—"} hours
          </span>
          <span className="text-slate-500">vs published</span>
        </div>
      ) : null}
    </div>
    </Tooltip>
  );
}

export function getSandboxHeaderMetrics(evaluation, stores) {
  if (!evaluation) return null;
  const ids = stores.map((store) => String(store.id));
  const metrics = (period) => period
    ? aggregateEvaluation(period.dailyTestResults || period.testResults, ids).target
    : null;
  return {
    earned: metrics(evaluation.history),
    forecasted: metrics(evaluation.forecast || evaluation),
    laborPercentAvailable: stores.length > 0 && stores.every((store) => store.labor_percent_enabled),
  };
}

export function getPublishedHeaderMetrics(laborPlanSummary, stores) {
  const evaluation = buildPublishedEvaluation(laborPlanSummary, stores, 4, 4);
  if (!evaluation) return null;
  const storeIds = stores.map((store) => String(store.id));
  return {
    earned: aggregateEvaluation(evaluation.history.testResults, storeIds).target,
    forecasted: aggregateEvaluation(evaluation.forecast.testResults, storeIds).target,
    laborPercentAvailable: stores.length > 0 && stores.every((store) => store.labor_percent_enabled),
  };
}

function aggregateLaborPlanSeries(laborPlanSummary, selectedStoreIds) {
  const allSeries = Array.isArray(laborPlanSummary?.series)
    ? laborPlanSummary.series
    : [];
  const storeSeries = laborPlanSummary?.store_series || {};
  const allStoreIds = Object.keys(storeSeries);
  const selectedIds = (selectedStoreIds || []).map(String);
  if (
    selectedIds.length === 0 ||
    (selectedIds.length === allStoreIds.length &&
      selectedIds.every((storeId) => allStoreIds.includes(storeId)))
  ) {
    return allSeries;
  }

  return allSeries.map((row, index) => {
    const aggregate = {
      ...row,
      actual: { hours: 0, sales: 0 },
      earned: { hours: 0, sales: 0 },
      forecasted: { hours: 0, sales: 0 },
      planned: { hours: 0, sales: 0 },
    };
    selectedIds.forEach((storeId) => {
      const storeRow = storeSeries[storeId]?.[index];
      ["actual", "earned", "forecasted", "planned"].forEach((key) => {
        aggregate[key].hours += numberValue(storeRow?.[key]?.hours);
        aggregate[key].sales += numberValue(storeRow?.[key]?.sales);
      });
    });
    return aggregate;
  });
}

function buildEvaluationChartSeries(historyDaily, forecastDaily) {
  const rows = [];

  historyDaily.forEach((day, date) => {
    rows.push({
      date,
      period: "historical",
      actual: { hours: day.comparisonHours, sales: day.sales },
      earned: { hours: null, sales: null },
      forecasted: { hours: null, sales: null },
      planned: { hours: null, sales: null },
      scenario: { hours: day.targetHours, sales: day.sales },
    });
  });
  forecastDaily.forEach((day, date) => {
    rows.push({
      date,
      period: "forecast",
      actual: { hours: null, sales: null },
      earned: { hours: null, sales: null },
      forecasted: { hours: day.comparisonHours, sales: day.sales },
      planned: { hours: day.comparisonHours, sales: day.sales },
      scenario: { hours: day.targetHours, sales: day.sales },
    });
  });

  return rows.sort((left, right) => String(left.date).localeCompare(String(right.date)));
}

export function buildChartSeries(
  laborPlanSummary,
  selectedStoreIds,
  historyEvaluation,
  historyDaily,
  forecastEvaluation,
  forecastDaily
) {
  const sourceRows = aggregateLaborPlanSeries(
    laborPlanSummary,
    selectedStoreIds
  );
  if (!sourceRows.length) {
    return buildEvaluationChartSeries(historyDaily, forecastDaily);
  }
  const forecastStartDate =
    laborPlanSummary?.series_window?.forecast_start_date;
  const isInsideEvaluation = (row, evaluation) =>
    Boolean(
      evaluation?.dateStart &&
        evaluation?.dateEnd &&
        row.date >= evaluation.dateStart &&
        row.date <= evaluation.dateEnd
    );

  return sourceRows
    .filter(
      (row) =>
        isInsideEvaluation(row, historyEvaluation) ||
        isInsideEvaluation(row, forecastEvaluation)
    )
    .map((row) => {
    const isForecast = forecastStartDate
      ? row.date >= forecastStartDate
      : row.period === "forecast";
    const normalizedRow = {
      ...row,
      period: isForecast ? "forecast" : "historical",
    };
    const evaluation =
      isForecast ? forecastEvaluation : historyEvaluation;
    const daily = isForecast ? forecastDaily : historyDaily;
    const inEvaluationWindow = Boolean(
      evaluation?.dateStart &&
        evaluation?.dateEnd &&
        row.date >= evaluation.dateStart &&
        row.date <= evaluation.dateEnd
    );
    if (!inEvaluationWindow) {
      return {
        ...normalizedRow,
        scenario: { hours: null, sales: null },
      };
    }
    const day = daily.get(row.date);
    if (!isForecast) {
      return {
        ...normalizedRow,
        scenario: {
          hours: day?.targetHours ?? null,
          sales: day?.sales ?? null,
        },
      };
    }
    return {
      ...normalizedRow,
      forecasted: {
        hours: day?.comparisonHours ?? null,
        sales: day?.sales ?? null,
      },
      planned: {
        hours: day?.comparisonHours ?? null,
        sales: day?.sales ?? null,
      },
      scenario: {
        hours: day?.targetHours ?? null,
        sales: day?.sales ?? null,
      },
    };
    });
}

function getHistoricalPublishedMetrics(
  laborPlanSummary,
  selectedStoreIds,
  dateStart,
  dateEnd
) {
  const selectedSet = new Set((selectedStoreIds || []).map(String));
  const stores = {};
  const totals = {
    actualHours: 0,
    actualCost: 0,
    earnedHours: 0,
    earnedCost: 0,
    sales: 0,
  };
  const storeSummaries = Object.fromEntries(
    (laborPlanSummary?.stores || []).map((store) => [
      String(store.store_id),
      store,
    ])
  );

  Object.entries(laborPlanSummary?.store_series || {}).forEach(
    ([storeId, rows]) => {
      if (selectedSet.size > 0 && !selectedSet.has(String(storeId))) return;
      const storeSummary = storeSummaries[String(storeId)];
      const actualHourlyCost = hourlyCost(storeSummary?.actual);
      const earnedHourlyCost = hourlyCost(storeSummary?.earned);
      const storeTotals = {
        actualHours: 0,
        actualCost: 0,
        earnedHours: 0,
        earnedCost: 0,
        sales: 0,
      };
      (rows || []).forEach((row) => {
        if (
          row.period !== "historical" ||
          (dateStart && row.date < dateStart) ||
          (dateEnd && row.date > dateEnd)
        ) {
          return;
        }
        storeTotals.actualHours += numberValue(row.actual?.hours);
        storeTotals.actualCost = addCosts(storeTotals.actualCost, costForHours(row.actual?.hours, actualHourlyCost));
        storeTotals.earnedHours += numberValue(row.earned?.hours);
        storeTotals.earnedCost = addCosts(storeTotals.earnedCost, costForHours(row.earned?.hours, earnedHourlyCost));
        storeTotals.sales += numberValue(row.actual?.sales);
      });
      stores[String(storeId)] = storeTotals;
      totals.actualHours += storeTotals.actualHours;
      totals.actualCost = addCosts(totals.actualCost, storeTotals.actualCost);
      totals.earnedHours += storeTotals.earnedHours;
      totals.earnedCost = addCosts(totals.earnedCost, storeTotals.earnedCost);
      totals.sales += storeTotals.sales;
    }
  );

  return { ...totals, stores };
}

export function getLaborModelImpactDeltas(
  evaluation,
  laborPlanSummary,
  selectedStoreIds = []
) {
  if (!evaluation) {
    return { earned: null, forecasted: null };
  }

  const historyEvaluation = evaluation.history || null;
  const forecastEvaluation = evaluation.forecast || evaluation;
  const historySummary = historyEvaluation
    ? aggregateEvaluation(
        historyEvaluation.dailyTestResults || historyEvaluation.testResults,
        selectedStoreIds
      )
    : null;
  const forecastSummary = forecastEvaluation
    ? aggregateEvaluation(
        forecastEvaluation.dailyTestResults || forecastEvaluation.testResults,
        selectedStoreIds
      )
    : null;
  const evaluationLaborPlanSummary =
    evaluation.laborPlanSummary || laborPlanSummary;
  const publishedHistory = historyEvaluation
    ? getHistoricalPublishedMetrics(
        evaluationLaborPlanSummary,
        selectedStoreIds,
        historyEvaluation.dateStart,
        historyEvaluation.dateEnd
      )
    : null;

  return {
    earned: historySummary
      ? percentDelta(
          historySummary.target.hours,
          publishedHistory?.earnedHours
        )
      : null,
    forecasted: forecastSummary
      ? percentDelta(
          forecastSummary.target.hours,
          forecastSummary.comparison.hours
        )
      : null,
  };
}

function getActualCoverage(laborPlanSummary, selectedStoreIds) {
  const selectedSet = new Set((selectedStoreIds || []).map(String));
  const selectedStores = (laborPlanSummary?.stores || []).filter((store) =>
    selectedSet.has(String(store.store_id))
  );
  const availableStoreIds = new Set(
    selectedStores
      .filter(
        (store) =>
          numberValue(store.actual?.hours) > 0 &&
          numberValue(store.data_quality?.included_days) > 0
      )
      .map((store) => String(store.store_id))
  );

  return {
    availableStoreIds,
    availableCount: availableStoreIds.size,
    totalCount: selectedSet.size,
    complete:
      selectedSet.size > 0 && availableStoreIds.size === selectedSet.size,
  };
}

function getStoreAdjustmentLabel(adjustment) {
  if (!adjustment) return null;
  const mode = adjustment.labor_budget_mode;
  const targetValue = Number(adjustment.labor_budget_target_value);
  if (mode === "target_splh" && Number.isFinite(targetValue)) {
    return `Fixed ${formatNumber(targetValue, 2)} SPLH`;
  }
  if (mode === "target_labor_percent" && Number.isFinite(targetValue)) {
    return `Fixed ${formatNumber(targetValue, 2)}% labor`;
  }
  const fitFactor = Number(adjustment.labor_model_fit_factor ?? 1);
  if (Number.isFinite(fitFactor) && Math.abs(fitFactor - 1) >= 0.0005) {
    return `Dynamic ${formatDelta((fitFactor - 1) * 100)}`;
  }
  return null;
}

export function getEffectiveStoreLaborBudgetMode(adjustment, store) {
  const adjustmentDefinesMode = Boolean(
    adjustment &&
      Object.prototype.hasOwnProperty.call(adjustment, "labor_budget_mode")
  );
  const mode = adjustmentDefinesMode
    ? adjustment.labor_budget_mode
    : store?.labor_budget_mode;
  return mode || "bottom_up";
}

function StorePeriodMetricCard({
  label,
  value,
  primaryMetricLabel,
  metrics,
  laborPercentAvailable,
  comparisonStatements,
  comparisonSubject,
}) {
  return (
    <div className="flex h-full min-w-0 flex-col rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <SummaryLaborMetrics
        value={value}
        primaryMetricLabel={primaryMetricLabel}
        metrics={metrics}
        laborPercentAvailable={laborPercentAvailable}
        compact
      />
      {comparisonStatements !== null ? (
        <LaborHoursComparisonStatement
          comparisons={comparisonStatements}
          compact
          subject={comparisonSubject}
        />
      ) : null}
    </div>
  );
}

export function getStorePlanComparisonMetrics(row) {
  const historyDayCount = numberValue(row?.historyDayCount);
  const forecastDayCount = numberValue(row?.forecastDayCount);
  const actualAvailable =
    row?.actualDataAvailable && Boolean(row?.history) && historyDayCount > 0;
  const earnedAvailable = Boolean(row?.history) && historyDayCount > 0;
  const publishedHistoryAvailable =
    Boolean(row?.publishedEarnedMetrics) && historyDayCount > 0;
  const forecastAvailable = Boolean(row?.forecast) && forecastDayCount > 0;
  const publishedForecastAvailable =
    Boolean(row?.forecast?.comparison) && forecastDayCount > 0;
  const laborPercentAvailable = Boolean(row?.laborPercentAvailable);

  return {
    actualSplh:
      actualAvailable && numberValue(row.history.comparison.hours) > 0
        ? row.history.comparison.splh
        : null,
    earnedSplh:
      earnedAvailable && numberValue(row.history.target.hours) > 0
        ? row.history.target.splh
        : null,
    publishedHistorySplh:
      publishedHistoryAvailable &&
      numberValue(row.publishedEarnedMetrics.hours) > 0
        ? row.publishedEarnedMetrics.splh
        : null,
    forecastSplh:
      forecastAvailable && numberValue(row.forecast.target.hours) > 0
        ? row.forecast.target.splh
        : null,
    publishedForecastSplh:
      publishedForecastAvailable &&
      numberValue(row.forecast.comparison.hours) > 0
        ? row.forecast.comparison.splh
        : null,
    actualHoursPerDay:
      actualAvailable && historyDayCount > 0
        ? row.history.comparison.hours / historyDayCount
        : null,
    earnedHoursPerDay:
      earnedAvailable && historyDayCount > 0
        ? row.history.target.hours / historyDayCount
        : null,
    publishedHistoryHoursPerDay:
      publishedHistoryAvailable && historyDayCount > 0
        ? row.publishedEarnedMetrics.hours / historyDayCount
        : null,
    forecastHoursPerDay:
      forecastAvailable && forecastDayCount > 0
        ? row.forecast.target.hours / forecastDayCount
        : null,
    publishedForecastHoursPerDay:
      publishedForecastAvailable && forecastDayCount > 0
        ? row.forecast.comparison.hours / forecastDayCount
        : null,
    actualLaborPercent:
      actualAvailable && laborPercentAvailable
        ? row.history.comparison.laborPercent
        : null,
    earnedLaborPercent:
      earnedAvailable && laborPercentAvailable
        ? row.history.target.laborPercent
        : null,
    publishedHistoryLaborPercent:
      publishedHistoryAvailable && laborPercentAvailable
        ? row.publishedEarnedMetrics.laborPercent
        : null,
    forecastLaborPercent:
      forecastAvailable && laborPercentAvailable
        ? row.forecast.target.laborPercent
        : null,
    publishedForecastLaborPercent:
      publishedForecastAvailable && laborPercentAvailable
        ? row.forecast.comparison.laborPercent
        : null,
    historicalSandboxGapPercent:
      earnedAvailable && publishedHistoryAvailable
        ? percentDelta(
            row.history.target.hours,
            row.publishedEarnedMetrics.hours
          )
        : null,
    historicalSandboxVsActualGapPercent:
      actualAvailable && earnedAvailable
        ? percentDelta(
            row.history.target.hours,
            row.history.comparison.hours
          )
        : null,
    futureSandboxGapPercent: forecastAvailable
      ? percentDelta(
          row.forecast.target.hours,
          row.forecast.comparison?.hours
        )
      : null,
  };
}

const STORE_PLAN_METRIC_CONFIG = {
  splh: {
    label: "SPLH",
    axisLabel: "Sales per labor hour",
    actualKey: "actualSplh",
    earnedKey: "earnedSplh",
    forecastKey: "forecastSplh",
    publishedHistoryKey: "publishedHistorySplh",
    publishedFutureKey: "publishedForecastSplh",
  },
  hours: {
    label: "Hours/day",
    axisLabel: "Average labor hours per day",
    actualKey: "actualHoursPerDay",
    earnedKey: "earnedHoursPerDay",
    forecastKey: "forecastHoursPerDay",
    publishedHistoryKey: "publishedHistoryHoursPerDay",
    publishedFutureKey: "publishedForecastHoursPerDay",
  },
  labor_percent: {
    label: "Labor %",
    axisLabel: "Labor percent",
    actualKey: "actualLaborPercent",
    earnedKey: "earnedLaborPercent",
    forecastKey: "forecastLaborPercent",
    publishedHistoryKey: "publishedHistoryLaborPercent",
    publishedFutureKey: "publishedForecastLaborPercent",
  },
};

function getStorePlanMetricConfig(metricKey) {
  return STORE_PLAN_METRIC_CONFIG[metricKey] || STORE_PLAN_METRIC_CONFIG.splh;
}

export function getStorePlanMetricValue(metrics, metricKey, seriesKey) {
  const config = getStorePlanMetricConfig(metricKey);
  return metrics?.[config[`${seriesKey}Key`]] ?? null;
}

function formatStorePlanMetricValue(value, metricKey, compact = false) {
  if (value === null || value === undefined) return "—";
  if (metricKey === "labor_percent") return formatPercent(value);
  if (metricKey === "hours") {
    return `${formatNumber(value, 1)}${compact ? "" : " hrs"}`;
  }
  return formatCurrency(value, compact ? 1 : 2);
}

function aggregateStorePlanMetric(rows, getMetric) {
  return rows.reduce(
    (total, row) => {
      const metric = getMetric(row);
      total.hours += numberValue(metric?.hours);
      total.cost = addCosts(total.cost, availableLaborCost(metric?.hours, metric?.cost));
      total.sales += numberValue(metric?.sales);
      return total;
    },
    { hours: 0, cost: 0, sales: 0 }
  );
}

export function buildCompanyStorePlanRow(rows) {
  const actualDataAvailable =
    rows.length > 0 && rows.every((row) => row.actualDataAvailable);
  const laborPercentAvailable =
    rows.length > 0 && rows.every((row) => row.laborPercentAvailable);
  const historyDayCount = Math.max(
    0,
    ...rows.map((row) => numberValue(row.historyDayCount))
  );
  const forecastDayCount = Math.max(
    0,
    ...rows.map((row) => numberValue(row.forecastDayCount))
  );
  const actual = aggregateStorePlanMetric(
    rows,
    (row) => row.history.comparison
  );
  const earned = aggregateStorePlanMetric(rows, (row) => row.history.target);
  const forecast = aggregateStorePlanMetric(rows, (row) => row.forecast.target);
  const publishedForecast = aggregateStorePlanMetric(
    rows,
    (row) => row.forecast.comparison
  );
  const publishedHistory = aggregateStorePlanMetric(
    rows,
    (row) => row.publishedEarnedMetrics
  );
  const companyRow = {
    storeId: "company-total",
    storeName: "Company average",
    isCompanyTotal: true,
    actualDataAvailable,
    laborPercentAvailable,
    historyDayCount,
    forecastDayCount,
    history: {
      comparison: getMetricGroup(actual.hours, actual.cost, actual.sales),
      target: getMetricGroup(earned.hours, earned.cost, earned.sales),
    },
    publishedEarnedMetrics: getMetricGroup(
      publishedHistory.hours,
      publishedHistory.cost,
      publishedHistory.sales
    ),
    forecast: {
      target: getMetricGroup(forecast.hours, forecast.cost, forecast.sales),
      comparison: getMetricGroup(
        publishedForecast.hours,
        publishedForecast.cost,
        publishedForecast.sales
      ),
    },
  };

  const comparisonMetrics = getStorePlanComparisonMetrics(companyRow);
  const averageHoursPerDay = (getHours, getDayCount, isAvailable = () => true) => {
    const values = rows
      .filter(isAvailable)
      .map((row) => {
        const hours = numberValue(getHours(row));
        const dayCount = numberValue(getDayCount(row));
        return dayCount > 0 ? hours / dayCount : null;
      })
      .filter((value) => value !== null);
    return values.length
      ? values.reduce((total, value) => total + value, 0) / values.length
      : null;
  };

  comparisonMetrics.actualHoursPerDay = averageHoursPerDay(
    (row) => row.history.comparison.hours,
    (row) => row.historyDayCount,
    (row) => row.actualDataAvailable
  );
  comparisonMetrics.earnedHoursPerDay = averageHoursPerDay(
    (row) => row.history.target.hours,
    (row) => row.historyDayCount
  );
  comparisonMetrics.publishedHistoryHoursPerDay = averageHoursPerDay(
    (row) => row.publishedEarnedMetrics.hours,
    (row) => row.historyDayCount
  );
  comparisonMetrics.forecastHoursPerDay = averageHoursPerDay(
    (row) => row.forecast.target.hours,
    (row) => row.forecastDayCount
  );
  comparisonMetrics.publishedForecastHoursPerDay = averageHoursPerDay(
    (row) => row.forecast.comparison.hours,
    (row) => row.forecastDayCount
  );

  return {
    ...companyRow,
    comparisonMetrics,
  };
}

export function compareStorePlanRows(
  left,
  right,
  sortBy = "gap",
  metricKey = "splh",
  comparisonMode = "history"
) {
  if (sortBy === "store") {
    return String(left.storeName || "").localeCompare(
      String(right.storeName || "")
    );
  }

  const getSortValue = (row) => {
    if (sortBy === "metric") {
      return getStorePlanMetricValue(
        row.comparisonMetrics,
        metricKey,
        comparisonMode === "history" ? "earned" : "forecast"
      );
    }
    const gap =
      comparisonMode === "history"
        ? sortBy === "actual_gap"
          ? row.comparisonMetrics.historicalSandboxVsActualGapPercent
          : row.comparisonMetrics.historicalSandboxGapPercent
        : row.comparisonMetrics.futureSandboxGapPercent;
    return gap === null || gap === undefined ? null : Math.abs(gap);
  };
  const leftValue = getSortValue(left);
  const rightValue = getSortValue(right);

  if (
    leftValue !== null &&
    rightValue !== null &&
    Math.abs(leftValue - rightValue) > 0.0001
  ) {
    return rightValue - leftValue;
  }
  if (leftValue !== null && rightValue === null) return -1;
  if (leftValue === null && rightValue !== null) return 1;
  return String(left.storeName || "").localeCompare(
    String(right.storeName || "")
  );
}

function StorePlanComparisonLegend({ laborLabel, comparisonMode, isPublishedTarget }) {
  const historyMode = comparisonMode === "history";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs font-medium text-slate-500">
      {historyMode ? (
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-violet-700" />
          Actual
        </div>
      ) : null}
      {isPublishedTarget ? (
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rotate-45 border-2 border-slate-500 bg-white" />
          Published
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full border-2 border-orange-600 bg-white" />
            {laborLabel}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rotate-45 border-2 border-slate-500 bg-white" />
            Published
          </div>
        </>
      )}
    </div>
  );
}

function RightworkSelect({
  id,
  label,
  options,
  value,
  onChange,
  className = "",
  showLabel = true,
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const optionRefs = React.useRef([]);
  const selectedOption =
    options.find((option) => option.value === value) || options[0];
  const listboxId = `${id}-options`;

  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const focusOption = (index) => {
    const normalizedIndex = (index + options.length) % options.length;
    optionRefs.current[normalizedIndex]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      window.requestAnimationFrame(() => {
        const selectedIndex = Math.max(
          0,
          options.findIndex((option) => option.value === value)
        );
        focusOption(selectedIndex);
      });
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleOptionKeyDown = (event, index) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(options.length - 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`flex h-10 w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 text-left text-xs font-semibold text-slate-700 transition-colors hover:border-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
          open ? "border-violet-500 ring-2 ring-violet-100" : "border-slate-300"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={`${label}: ${selectedOption?.label || ""}`}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="truncate">
          {showLabel ? `${label}: ` : ""}
          {selectedOption?.label}
        </span>
        <ExpandMoreIcon
          className={`shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          sx={{ fontSize: 18 }}
        />
      </button>

      {open ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={label}
          className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-full overflow-hidden rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl"
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                aria-selected={selected}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                  selected
                    ? "bg-violet-50 text-violet-800"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
              >
                <span className="w-4 shrink-0">
                  {selected ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </span>
                <span className="whitespace-nowrap">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function StorePlanComparisonPlot({
  rows,
  laborLabel,
  metricKey,
  comparisonMode,
  periodLabel,
  isPublishedTarget,
}) {
  const metricConfig = getStorePlanMetricConfig(metricKey);
  const historyMode = comparisonMode === "history";
  const seriesKeys = isPublishedTarget
    ? historyMode
      ? ["actual", "earned"]
      : ["forecast"]
    : historyMode
      ? ["actual", "earned", "publishedHistory"]
      : ["forecast", "publishedFuture"];
  const plottedValues = rows.flatMap((row) =>
    seriesKeys
      .map((seriesKey) =>
        getStorePlanMetricValue(
          row.comparisonMetrics,
          metricKey,
          seriesKey
        )
      )
      .filter((value) => value !== null)
  );
  const rawMin = plottedValues.length ? Math.min(...plottedValues) : 0;
  const rawMax = plottedValues.length ? Math.max(...plottedValues) : 1;
  const rawRange = rawMax - rawMin;
  const padding = rawRange > 0 ? rawRange * 0.08 : Math.max(rawMax * 0.08, 1);
  const axisMin = Math.max(0, rawMin - padding);
  const axisMax = rawMax + padding;
  const axisRange = axisMax - axisMin || 1;
  const ticks = Array.from(
    { length: 5 },
    (_, index) => axisMin + (axisRange * index) / 4
  );
  const getPosition = (value) =>
    value === null ? null : ((value - axisMin) / axisRange) * 100;
  const gridTemplateColumns = isPublishedTarget
    ? historyMode
      ? "190px minmax(420px, 1fr) 140px"
      : "190px minmax(480px, 1fr)"
    : historyMode
      ? "190px minmax(360px, 1fr) 120px 120px"
      : "190px minmax(380px, 1fr) 120px";
  const gapHeaders = isPublishedTarget
    ? historyMode
      ? ["Published vs actual"]
      : []
    : historyMode
      ? [`${laborLabel} vs published`, `${laborLabel} vs actual`]
      : [`${laborLabel} vs published`];

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div className={historyMode ? "min-w-[850px]" : "min-w-[680px]"}>
        <div
          className="grid items-end gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-normal text-slate-500"
          style={{ gridTemplateColumns }}
        >
          <span>Store</span>
          <div>
            <span>{metricConfig.axisLabel}</span>
            <div className="relative mt-2 h-4 font-medium normal-case text-slate-400">
              {ticks.map((tick, index) => (
                <span
                  key={index}
                  className={`absolute whitespace-nowrap ${
                    index === 0
                      ? ""
                      : index === ticks.length - 1
                        ? "-translate-x-full"
                        : "-translate-x-1/2"
                  }`}
                  style={{ left: `${(index / 4) * 100}%` }}
                >
                  {formatStorePlanMetricValue(tick, metricKey, true)}
                </span>
              ))}
            </div>
          </div>
          {gapHeaders.map((header) => (
            <span key={header} className="text-right">
              {header}
            </span>
          ))}
        </div>

        {rows.map((row) => {
          const values = row.comparisonMetrics;
          const points = isPublishedTarget
            ? [
                ...(historyMode
                  ? [
                      {
                        key: "actual",
                        label: "Actual",
                        value: getStorePlanMetricValue(values, metricKey, "actual"),
                        className:
                          "z-20 h-3 w-3 rounded-full border-violet-700 bg-violet-700",
                      },
                    ]
                  : []),
                {
                  key: "published",
                  label: "Published",
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    historyMode ? "earned" : "forecast"
                  ),
                  className:
                    "z-10 h-[18px] w-[18px] rotate-45 border-slate-500 bg-white",
                },
              ]
            : historyMode
            ? [
                {
                  key: "actual",
                  label: "Actual",
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    "actual"
                  ),
                  className:
                    "z-20 h-3 w-3 rounded-full border-violet-700 bg-violet-700",
                  overlapClassName:
                    "z-30 h-1.5 w-1.5 rounded-full border-violet-700 bg-violet-700",
                },
                {
                  key: "sandbox",
                  label: laborLabel,
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    "earned"
                  ),
                  className: `z-20 h-4 w-4 rounded-full bg-white ${
                    laborLabel === "Sandbox"
                      ? "border-orange-600"
                      : "border-violet-500"
                  }`,
                  overlapClassName: `z-20 h-2.5 w-2.5 rounded-full bg-white ${
                    laborLabel === "Sandbox"
                      ? "border-orange-600"
                      : "border-violet-500"
                  }`,
                },
                {
                  key: "published",
                  label: "Published",
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    "publishedHistory"
                  ),
                  className:
                    "z-10 h-[18px] w-[18px] rotate-45 border-slate-500 bg-white",
                },
              ]
            : [
                {
                  key: "sandbox",
                  label: laborLabel,
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    "forecast"
                  ),
                  className: `z-20 h-4 w-4 rounded-full bg-white ${
                    laborLabel === "Sandbox"
                      ? "border-orange-600"
                      : "border-violet-500"
                  }`,
                  overlapClassName: `z-20 h-2.5 w-2.5 rounded-full bg-white ${
                    laborLabel === "Sandbox"
                      ? "border-orange-600"
                      : "border-violet-500"
                  }`,
                },
                {
                  key: "published",
                  label: "Published",
                  value: getStorePlanMetricValue(
                    values,
                    metricKey,
                    "publishedFuture"
                  ),
                  className:
                    "z-10 h-[18px] w-[18px] rotate-45 border-slate-500 bg-white",
                },
              ];
          const pointPositions = points.map((point) =>
            getPosition(point.value)
          );
          const availablePositions = pointPositions.filter(
            (position) => position !== null
          );
          const pointHasCollision = (pointIndex) =>
            pointPositions.some(
              (position, index) =>
                index !== pointIndex &&
                position !== null &&
                pointPositions[pointIndex] !== null &&
                Math.abs(position - pointPositions[pointIndex]) < 0.5
            );
          const rangeStart = availablePositions.length
            ? Math.min(...availablePositions)
            : null;
          const rangeEnd = availablePositions.length
            ? Math.max(...availablePositions)
            : null;
          const gaps = isPublishedTarget
            ? historyMode
              ? [
                  {
                    key: "actual",
                    value: values.historicalSandboxVsActualGapPercent,
                    label: "Published vs actual",
                    title: "Published hours versus actual hours",
                  },
                ]
              : []
            : historyMode
            ? [
                {
                  key: "sandbox",
                  value: values.historicalSandboxGapPercent,
                  label: `${laborLabel} vs published`,
                  title: `${laborLabel} hours versus published hours`,
                },
                {
                  key: "actual",
                  value: values.historicalSandboxVsActualGapPercent,
                  label: `${laborLabel} vs actual`,
                  title: `${laborLabel} hours versus actual hours`,
                },
              ]
            : [
                {
                  key: "sandbox",
                  value: values.futureSandboxGapPercent,
                  label: `${laborLabel} vs published`,
                  title: `${laborLabel} hours versus published hours`,
                },
              ];
          const rowTooltipLabel = [
            ...points
              .filter((point) => point.value !== null)
              .map(
                (point) =>
                  `${point.label}: ${formatStorePlanMetricValue(
                    point.value,
                    metricKey
                  )}`
              ),
            ...gaps.map(
              (gap) => `${gap.title}: ${formatDelta(gap.value)}`
            ),
          ].join(". ");
          const tooltipContent = (
            <div
              data-testid={`store-comparison-tooltip-${row.storeId}`}
              className="w-[320px] max-w-[calc(100vw-32px)] p-3 text-xs"
            >
              <div className="flex items-center justify-between gap-4 font-semibold text-slate-700">
                <p>{row.storeName}</p>
                <p className="shrink-0 whitespace-nowrap font-medium text-slate-500">
                  {periodLabel}
                </p>
              </div>
              <div className="mt-2 space-y-1.5">
                {points
                  .filter((point) => point.value !== null)
                  .map((point) => (
                    <div
                      key={point.key}
                      className="flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-2 text-slate-500">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 border-2 ${
                            point.key === "actual"
                              ? "rounded-full border-violet-700 bg-violet-700"
                              : point.key === "sandbox"
                                ? `rounded-full bg-white ${
                                    laborLabel === "Sandbox"
                                      ? "border-orange-600"
                                      : "border-violet-500"
                                  }`
                                : "rotate-45 border-slate-500 bg-white"
                          }`}
                        />
                        {point.label}
                      </div>
                      <p className="shrink-0 text-right font-semibold text-slate-900">
                        {formatStorePlanMetricValue(point.value, metricKey)}
                      </p>
                    </div>
                  ))}
              </div>
              <div className="mt-2 space-y-1.5 border-t border-slate-200 pt-2">
                {gaps.map((gap) => (
                  <div
                    key={gap.key}
                    className="flex items-center justify-between gap-4"
                  >
                    <p className="text-slate-500">{gap.label}</p>
                    <p className="shrink-0 text-right font-semibold text-slate-900">
                      <LaborHoursDeltaValue value={gap.value} />
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );

          return (
            <Tooltip
              key={row.storeId}
              title={tooltipContent}
              placement="top"
              enterDelay={100}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow:
                      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                    color: "inherit",
                    maxWidth: "none",
                    padding: 0,
                  },
                },
              }}
            >
              <div
                className={`relative grid min-h-[48px] items-center gap-4 border-b px-4 transition-colors last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                  row.isCompanyTotal
                    ? "border-violet-200 bg-violet-50/70 hover:bg-violet-100/80"
                    : "border-slate-100 hover:bg-violet-50"
                }`}
                style={{ gridTemplateColumns }}
                role="group"
                aria-label={`${row.storeName}. ${rowTooltipLabel}`}
                tabIndex={0}
              >
              <span
                className={`truncate text-sm font-semibold ${
                  row.isCompanyTotal ? "text-violet-950" : "text-slate-900"
                }`}
              >
                {row.storeName}
              </span>
              <div className="relative h-8">
                {ticks.map((_, index) => (
                  <span
                    key={index}
                    className="absolute inset-y-0 border-l border-slate-100"
                    style={{ left: `${(index / 4) * 100}%` }}
                  />
                ))}
                {rangeStart !== null && rangeEnd !== null ? (
                  <span
                    className="absolute top-1/2 h-px -translate-y-1/2 bg-slate-300"
                    style={{
                      left: `${rangeStart}%`,
                      width: `${rangeEnd - rangeStart}%`,
                    }}
                  />
                ) : null}
                {points.map((point, pointIndex) => {
                  const position = pointPositions[pointIndex];
                  if (position === null) return null;
                  const formattedValue = formatStorePlanMetricValue(
                    point.value,
                    metricKey
                  );
                  const markerClassName =
                    pointHasCollision(pointIndex) && point.overlapClassName
                      ? point.overlapClassName
                      : point.className;
                  const markerZIndex =
                    point.key === "actual"
                      ? "z-50"
                      : point.key === "sandbox"
                        ? "z-40"
                        : "z-30";
                  return (
                    <span
                      key={point.key}
                      className={`absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 ${markerZIndex}`}
                      style={{
                        left: `${position}%`,
                        top: "50%",
                      }}
                      role="img"
                      aria-label={`${point.label}: ${formattedValue}`}
                      data-nested={
                        pointHasCollision(pointIndex) ? "true" : undefined
                      }
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border-2 shadow-sm ${markerClassName}`}
                      />
                    </span>
                  );
                })}
              </div>
              {gaps.map((gap) => {
                const gapClassName =
                  gap.value === null || Math.abs(gap.value) < 0.05
                    ? "text-slate-500"
                    : gap.value > 0
                      ? "text-rose-600"
                      : "text-emerald-600";
                return (
                  <span
                    key={gap.key}
                    className={`text-right text-sm font-semibold tabular-nums ${gapClassName}`}
                  >
                    <LaborHoursDeltaValue value={gap.value} />
                  </span>
                );
              })}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

export function canCompareStoreLaborPercent(rows) {
  return (
    rows.length > 0 && rows.every((row) => row.laborPercentAvailable)
  );
}

function StorePlanCards({
  rows,
  refreshing,
  visibleSeries,
  isPublishedTarget,
  view,
  comparisonMode,
  onComparisonModeChange,
  historyDateRange,
  forecastDateRange,
}) {
  const laborLabel = isPublishedTarget ? "Published" : "Sandbox";
  const comparisonSubject = isPublishedTarget ? "Published labor" : "This sandbox";
  const initialVisibleCount = 5;
  const [searchQuery, setSearchQuery] = React.useState("");
  const [storePlanSort, setStorePlanSort] = React.useState("gap");
  const [comparisonMetric, setComparisonMetric] = React.useState("splh");
  const [visibleCount, setVisibleCount] = React.useState(initialVisibleCount);
  const laborPercentAvailable = canCompareStoreLaborPercent(rows);
  const selectedMetricConfig = getStorePlanMetricConfig(comparisonMetric);
  const metricOptions = [
    { value: "splh", label: "SPLH" },
    { value: "hours", label: "Hours/day" },
    ...(laborPercentAvailable
      ? [{ value: "labor_percent", label: "Labor Percent" }]
      : []),
  ];
  const sortOptions = [
    ...(!isPublishedTarget
      ? [{ value: "gap", label: `Largest ${laborLabel.toLowerCase()} gap` }]
      : []),
    ...(comparisonMode === "history"
      ? [
          {
            value: "actual_gap",
            label: `Largest ${laborLabel.toLowerCase()} vs actual gap`,
          },
        ]
      : []),
    {
      value: "metric",
      label: `Highest ${laborLabel.toLowerCase()} ${selectedMetricConfig.label}`,
    },
    { value: "store", label: "Store A–Z" },
  ];
  const sortedRows = React.useMemo(
    () =>
      rows
        .map((row) => ({
          ...row,
          comparisonMetrics: getStorePlanComparisonMetrics(row),
        }))
        .sort((left, right) =>
          compareStorePlanRows(
            left,
            right,
            storePlanSort,
            comparisonMetric,
            comparisonMode
          )
        ),
    [comparisonMetric, comparisonMode, rows, storePlanSort]
  );
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = React.useMemo(
    () =>
      normalizedQuery
        ? sortedRows.filter((row) =>
            row.storeName.toLowerCase().includes(normalizedQuery)
          )
        : sortedRows,
    [normalizedQuery, sortedRows]
  );
  const visibleRows =
    view === "comparison" || normalizedQuery
      ? filteredRows
      : filteredRows.slice(0, visibleCount);
  const companyRow = React.useMemo(
    () => buildCompanyStorePlanRow(rows),
    [rows]
  );
  const comparisonRows = [companyRow, ...visibleRows];

  React.useEffect(() => {
    setVisibleCount(initialVisibleCount);
  }, [normalizedQuery, rows.length]);

  React.useEffect(() => {
    if (!laborPercentAvailable && comparisonMetric === "labor_percent") {
      setComparisonMetric("splh");
    }
  }, [comparisonMetric, laborPercentAvailable]);

  React.useEffect(() => {
    if (isPublishedTarget && comparisonMode === "history" && storePlanSort === "gap") {
      setStorePlanSort("actual_gap");
      return;
    }
    if (
      comparisonMode === "future" &&
      (storePlanSort === "actual_gap" || (isPublishedTarget && storePlanSort === "gap"))
    ) {
      setStorePlanSort(isPublishedTarget ? "metric" : "gap");
    }
  }, [comparisonMode, isPublishedTarget, storePlanSort]);

  return (
    <div
      className={
        view === "comparison"
          ? "mt-2"
          : "mt-3 border-t border-slate-200 pt-2"
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3
            className={`font-semibold text-slate-900 ${
              view === "comparison" ? "text-lg" : "text-base"
            }`}
          >
            {view === "comparison" ? "Store comparison" : "Store plans"}
          </h3>
          {view === "comparison" ? (
            <StorePlanComparisonLegend
              laborLabel={laborLabel}
              comparisonMode={comparisonMode}
              isPublishedTarget={isPublishedTarget}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 xl:w-full xl:justify-start">
          {refreshing ? (
            <span className="mr-1 text-xs font-medium text-violet-700">
              Refreshing…
            </span>
          ) : null}
          {view === "comparison" ? (
            <>
              <RightworkSelect
                id="review-store-plan-period"
                label="Period"
                options={[
                  {
                    value: "history",
                    label: `Earned · ${historyDateRange}`,
                  },
                  {
                    value: "future",
                    label: `Forecasted · ${forecastDateRange}`,
                  },
                ]}
                value={comparisonMode}
                onChange={onComparisonModeChange}
                className="w-[260px]"
                showLabel={false}
              />
              <RightworkSelect
                id="review-store-plan-metric"
                label="Metric"
                options={metricOptions}
                value={comparisonMetric}
                onChange={setComparisonMetric}
                className="w-[120px]"
                showLabel={false}
              />
              <RightworkSelect
                id="review-store-plan-sort"
                label="Sort"
                options={sortOptions}
                value={storePlanSort}
                onChange={setStorePlanSort}
                className="w-[225px]"
                showLabel={false}
              />
            </>
          ) : null}
          {rows.length > 5 || searchQuery ? (
            <label className="relative block w-56 max-w-full">
              <span className="sr-only">Search stores</span>
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                fontSize="small"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search stores"
                className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
              />
            </label>
          ) : null}
        </div>
      </div>

      <div
        className={
          view === "comparison" ? "mt-3" : "mt-2 space-y-1.5"
        }
      >
        {view === "comparison" && rows.length ? (
          <StorePlanComparisonPlot
            rows={comparisonRows}
            laborLabel={laborLabel}
            metricKey={comparisonMetric}
            comparisonMode={comparisonMode}
            periodLabel={
              comparisonMode === "history"
                ? `Earned · ${historyDateRange}`
                : `Forecasted · ${forecastDateRange}`
            }
            isPublishedTarget={isPublishedTarget}
          />
        ) : null}
        {view === "overview"
          ? visibleRows.map((row) => (
              <article
                key={row.storeId}
                className="rounded-xl border border-slate-200 bg-slate-50/40 p-2"
              >
                <div className="grid grid-cols-[minmax(120px,0.55fr)_minmax(220px,1fr)_minmax(220px,1fr)_minmax(280px,1.35fr)] items-stretch gap-1.5 xl:grid-cols-[minmax(140px,0.6fr)_minmax(210px,1fr)_minmax(210px,1fr)] lg:grid-cols-2 sm:grid-cols-1">
                  <div className="flex min-w-0 items-center xl:col-span-3 lg:col-span-2 sm:col-span-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h4 className="truncate text-sm font-semibold text-slate-950">
                        {row.storeName}
                      </h4>
                      {row.adjustmentLabel ? (
                        <span className="rounded-md border border-violet-100 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                          {row.adjustmentLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <StorePeriodMetricCard
                    label={`${laborLabel} earned labor`}
                    metrics={row.history.target}
                    laborPercentAvailable={row.laborPercentAvailable}
                    value={formatPrimaryMetric(
                      row.history.target,
                      row.primaryMetricKey
                    )}
                    primaryMetricLabel={getPrimaryMetricLabel(
                      row.primaryMetricKey
                    )}
                    comparisonStatements={[
                      !isPublishedTarget
                        ? getLaborHoursPercentComparison(
                            row.history.target.hours,
                            row.publishedEarnedMetrics.hours,
                            "the published model"
                          )
                        : null,
                      row.actualDataAvailable
                        ? getLaborHoursPercentComparison(
                            row.history.target.hours,
                            row.history.comparison.hours,
                            "actual used labor"
                          )
                        : null,
                    ]}
                    comparisonSubject={comparisonSubject}
                  />
                  <StorePeriodMetricCard
                    label={`${laborLabel} forecasted labor`}
                    metrics={row.forecast.target}
                    laborPercentAvailable={row.laborPercentAvailable}
                    value={formatPrimaryMetric(
                      row.forecast.target,
                      row.primaryMetricKey
                    )}
                    primaryMetricLabel={getPrimaryMetricLabel(
                      row.primaryMetricKey
                    )}
                    comparisonStatements={
                      isPublishedTarget
                        ? null
                        : [
                            getLaborHoursPercentComparison(
                              row.forecast.target.hours,
                              row.forecast.comparison.hours,
                              "the published model"
                            ),
                          ]
                    }
                    comparisonSubject={comparisonSubject}
                  />
                  <div className="flex min-w-0 items-center xl:col-span-3 lg:col-span-2 sm:col-span-1">
                    <StoreTrendSparkline
                      seriesRows={row.seriesRows}
                      scenarioSeriesRows={isPublishedTarget ? null : row.seriesRows}
                      scenarioLabel={laborLabel}
                      visibleSeries={visibleSeries}
                      consolidatePublishedSeries={!isPublishedTarget}
                      dense
                    />
                  </div>
                </div>
              </article>
            ))
          : null}
        {filteredRows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
            {normalizedQuery
              ? "No stores match this search."
              : "No store results are available for the selected stores."}
          </div>
        ) : null}
        {view === "overview" &&
        !normalizedQuery &&
        filteredRows.length > initialVisibleCount ? (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              onClick={() =>
                setVisibleCount((current) =>
                  current >= filteredRows.length
                    ? initialVisibleCount
                    : filteredRows.length
                )
              }
            >
              {visibleCount >= filteredRows.length
                ? "Show fewer stores"
                : `Show ${filteredRows.length - visibleCount} more stores`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const PUBLISHED_PLAN_COMPARISON = {
  type: "published_plan",
  uuid: null,
  label: "Published labor plan",
};

const REVIEW_RANGE_OPTIONS = [
  { value: "short", label: "Short · 1 wk back / 1 wk forward", weeksBack: 1, weeksForward: 1 },
  { value: "medium", label: "Medium · 2 wks back / 2 wks forward", weeksBack: 2, weeksForward: 2 },
  { value: "full", label: "Full · 4 wks back / 4 wks forward", weeksBack: 4, weeksForward: 4 },
];

function getEvaluationKey(target, publishedPlanKey, rangePreset = "full") {
  if (!target?.uuid) return null;
  return [target.type, target.uuid, publishedPlanKey, rangePreset].join(":");
}

function LaborModelPlanSummary({
  stepNumber,
  target,
  stores = [],
  publications = [],
  evaluations = {},
  evaluationLoadingKey,
  evaluationStage,
  evaluationErrors = {},
  laborPlanSummary,
  laborPlanSummaryError = "",
  activePublicationUuid,
  onRetryPublishedPlan,
  onPublishedRangeChange,
  storeAdjustments = [],
  onEvaluate,
  onAdvancedAnalysis,
  expandRequest,
  embeddedReviewPanel = false,
}) {
  const isPublishedTarget = target?.type === "publication";
  const selectedPublication = publications.find(
    (publication) => publication.uuid === target?.uuid
  );
  const selectedPublicationStatus = selectedPublication
    ? getPublicationStatus(selectedPublication, activePublicationUuid)
    : null;
  const publishedPlanUnavailable = Boolean(
    isPublishedTarget &&
      activePublicationUuid &&
      target?.uuid !== activePublicationUuid &&
      selectedPublicationStatus?.tone !== "scheduled"
  );
  const laborLabel = isPublishedTarget ? "Published" : "Sandbox";
  const comparisonSubject = isPublishedTarget ? "Published labor" : "This sandbox";
  const allStoreIds = React.useMemo(
    () => stores.map((store) => String(store.id)),
    [stores]
  );
  const [selectedStoreIds, setSelectedStoreIds] = React.useState(allStoreIds);
  const [visibleSeries, setVisibleSeries] = React.useState({
    actual: true,
    published: true,
    scenario: true,
  });
  const [laborPlanView, setLaborPlanView] = React.useState("comparison");
  const [comparisonPeriod, setComparisonPeriod] = React.useState("history");
  const [reviewRangePreset, setReviewRangePreset] = React.useState("full");
  const [summaryExpanded, setSummaryExpanded] = React.useState(() => {
    try {
      return (
        window.sessionStorage.getItem(SUMMARY_EXPANDED_STORAGE_KEY) !== "false"
      );
    } catch (error) {
      return true;
    }
  });
  const summaryContentId = React.useId();

  React.useEffect(() => {
    setSelectedStoreIds(allStoreIds);
  }, [allStoreIds]);

  const toggleSeries = (seriesKey) => {
    setVisibleSeries((current) => ({
      ...current,
      [seriesKey]: current[seriesKey] === false,
    }));
  };

  React.useEffect(() => {
    if (expandRequest) setSummaryExpanded(true);
  }, [expandRequest]);

  React.useEffect(() => {
    try {
      window.sessionStorage.setItem(
        SUMMARY_EXPANDED_STORAGE_KEY,
        String(summaryExpanded)
      );
    } catch (error) {
      // The preference is optional when browser storage is unavailable.
    }
  }, [summaryExpanded]);

  const publishedPlanKey = React.useMemo(
    () =>
      publications
        .map(
          (publication) =>
            `${publication.uuid}:${publication.effective_date || "legacy"}`
        )
        .sort()
        .join("|") || "published-plan",
    [publications]
  );
  const publicationMarkers = React.useMemo(
    () =>
      publications
        .filter((publication) => publication.effective_date)
        .map((publication) => ({
          date: publication.effective_date,
          label: "Model update",
          description: `${getPublicationDisplayName(publication)} took effect`,
        }))
        .sort((left, right) => left.date.localeCompare(right.date)),
    [publications]
  );
  const evaluationKey = getEvaluationKey(
    target,
    publishedPlanKey,
    reviewRangePreset
  );
  const selectedRange =
    REVIEW_RANGE_OPTIONS.find((option) => option.value === reviewRangePreset) ||
    REVIEW_RANGE_OPTIONS[REVIEW_RANGE_OPTIONS.length - 1];
  const handleReviewRangeChange = (rangePreset) => {
    setReviewRangePreset(rangePreset);
    if (
      !isPublishedTarget ||
      publishedPlanUnavailable ||
      !onPublishedRangeChange
    ) {
      return;
    }
    const range =
      REVIEW_RANGE_OPTIONS.find((option) => option.value === rangePreset) ||
      REVIEW_RANGE_OPTIONS[REVIEW_RANGE_OPTIONS.length - 1];
    onPublishedRangeChange(range);
  };
  const requestedEvaluation = evaluationKey ? evaluations[evaluationKey] : null;
  const publishedEvaluation = React.useMemo(
    () =>
      isPublishedTarget && !publishedPlanUnavailable
        ? buildPublishedEvaluation(
            laborPlanSummary,
            stores,
            selectedRange.weeksBack,
            selectedRange.weeksForward
          )
        : null,
    [
      isPublishedTarget,
      publishedPlanUnavailable,
      laborPlanSummary,
      selectedRange.weeksBack,
      selectedRange.weeksForward,
      stores,
    ]
  );
  const evaluation = isPublishedTarget
    ? publishedEvaluation
    : requestedEvaluation;
  const evaluating = !isPublishedTarget && evaluationLoadingKey === evaluationKey;
  const evaluationError = evaluationKey
    ? evaluationErrors[evaluationKey]
    : null;
  const targetHasChanged =
    evaluation?.target?.type === "scenario" &&
    Number(evaluation.target.revision) !== Number(target?.revision);
  const resultsNeedRefresh = Boolean(evaluation && targetHasChanged);
  const resultsAreCurrent = Boolean(
    evaluation && !resultsNeedRefresh && !evaluationError && !evaluating
  );
  const activeLaborPlanSummary =
    evaluation?.laborPlanSummary || laborPlanSummary;

  const forecastEvaluation = evaluation?.forecast || evaluation;
  const historyEvaluation = evaluation?.history || null;
  const forecastSummary = React.useMemo(
    () =>
      aggregateEvaluation(
        forecastEvaluation?.dailyTestResults ||
          forecastEvaluation?.testResults,
        selectedStoreIds
      ),
    [forecastEvaluation, selectedStoreIds]
  );
  const historySummary = React.useMemo(
    () =>
      aggregateEvaluation(
        historyEvaluation?.dailyTestResults || historyEvaluation?.testResults,
        selectedStoreIds
      ),
    [historyEvaluation, selectedStoreIds]
  );
  const publishedHistory = React.useMemo(
    () =>
      getHistoricalPublishedMetrics(
        activeLaborPlanSummary,
        selectedStoreIds,
        historyEvaluation?.dateStart,
        historyEvaluation?.dateEnd
      ),
    [
      historyEvaluation?.dateEnd,
      historyEvaluation?.dateStart,
      activeLaborPlanSummary,
      selectedStoreIds,
    ]
  );
  const actualCoverage = React.useMemo(
    () => getActualCoverage(activeLaborPlanSummary, selectedStoreIds),
    [activeLaborPlanSummary, selectedStoreIds]
  );
  const adjustmentsByStoreId = React.useMemo(
    () =>
      Object.fromEntries(
        storeAdjustments.map((adjustment) => [
          String(adjustment.store_id),
          adjustment,
        ])
      ),
    [storeAdjustments]
  );
  const laborPercentAvailable = selectedStoreIds.length > 0 &&
    selectedStoreIds.every((storeId) =>
      stores.find((store) => String(store.id) === String(storeId))?.labor_percent_enabled
    );
  const primaryMetricKey = React.useMemo(() => {
    if (!laborPercentAvailable) return "splh";
    const storesById = Object.fromEntries(
      stores.map((store) => [String(store.id), store])
    );
    const selectedModes = selectedStoreIds.map((storeId) =>
      getEffectiveStoreLaborBudgetMode(
        adjustmentsByStoreId[String(storeId)],
        storesById[String(storeId)]
      )
    );
    return selectedModes.every((mode) => mode === "target_labor_percent")
      ? "laborPercent"
      : "splh";
  }, [adjustmentsByStoreId, selectedStoreIds, stores, laborPercentAvailable]);
  const chartRows = React.useMemo(
    () =>
      buildChartSeries(
        activeLaborPlanSummary,
        selectedStoreIds,
        historyEvaluation,
        historySummary.daily,
        forecastEvaluation,
        forecastSummary.daily
      ),
    [
      forecastEvaluation,
      forecastSummary.daily,
      historyEvaluation,
      historySummary.daily,
      activeLaborPlanSummary,
      selectedStoreIds,
    ]
  );
  const scenarioChartRows = React.useMemo(
    () => chartRows.map((row) => ({ ...row })),
    [chartRows]
  );
  const storePlanRows = React.useMemo(() => {
    const selectedSet = new Set(selectedStoreIds.map(String));
    return stores
      .filter((store) => selectedSet.has(String(store.id)))
      .map((store) => {
        const storeId = String(store.id);
        const historyAggregate = aggregateEvaluation(
          historyEvaluation?.dailyTestResults ||
            historyEvaluation?.testResults,
          [storeId]
        );
        const forecastAggregate = aggregateEvaluation(
          forecastEvaluation?.dailyTestResults ||
            forecastEvaluation?.testResults,
          [storeId]
        );
        const history = historyAggregate.stores[0];
        const forecast = forecastAggregate.stores[0];
        const published = publishedHistory.stores[storeId];
        const adjustment = adjustmentsByStoreId[storeId];
        const storePrimaryMetricKey =
          store.labor_percent_enabled &&
          getEffectiveStoreLaborBudgetMode(adjustment, store) ===
          "target_labor_percent"
            ? "laborPercent"
            : "splh";
        const actualDataAvailable =
          actualCoverage.availableStoreIds.has(storeId);
        const seriesRows = buildChartSeries(
          activeLaborPlanSummary,
          [storeId],
          historyEvaluation,
          historyAggregate.daily,
          forecastEvaluation,
          forecastAggregate.daily
        ).map((row) =>
          !actualDataAvailable && row.period === "historical"
            ? { ...row, actual: { hours: null, sales: null } }
            : row
        );
        return {
          storeId,
          storeName: store.store_name || store.storeName || store.name,
          history,
          forecast,
          publishedEarnedMetrics: getMetricGroup(
            published?.earnedHours,
            published?.earnedCost,
            published?.sales
          ),
          primaryMetricKey: storePrimaryMetricKey,
          actualDataAvailable,
          laborPercentAvailable: Boolean(store.labor_percent_enabled),
          historyDayCount: historyAggregate.daily.size,
          forecastDayCount: forecastAggregate.daily.size,
          adjustmentLabel: getStoreAdjustmentLabel(adjustment),
          seriesRows,
        };
      })
      .filter((row) => row.history && row.forecast);
  }, [
    actualCoverage.availableStoreIds,
    adjustmentsByStoreId,
    forecastEvaluation,
    historyEvaluation,
    activeLaborPlanSummary,
    publishedHistory.stores,
    selectedStoreIds,
    stores,
  ]);
  const publishedEarnedMetrics = getMetricGroup(
    publishedHistory.earnedHours,
    publishedHistory.earnedCost,
    publishedHistory.sales
  );
  const earnedImpactDelta = percentDelta(
    historySummary.target.hours,
    publishedEarnedMetrics.hours
  );
  const forecastImpactDelta = percentDelta(
    forecastSummary.target.hours,
    forecastSummary.comparison.hours
  );
  const evaluationButtonLabel =
    evaluationStage === "history"
      ? evaluation
        ? "Recalculating earned…"
        : "Calculating earned…"
      : evaluationStage === "forecast"
        ? evaluation
          ? "Recalculating forecast…"
          : "Calculating forecast…"
        : evaluation
          ? "Recalculating…"
          : "Calculating…";
  const refreshEvaluation = () => {
    if (isPublishedTarget || !evaluationKey || !onEvaluate || evaluating) return;
    onEvaluate({
      evaluationKey,
      target,
      comparison: PUBLISHED_PLAN_COMPARISON,
      storeIds: allStoreIds,
      rangePreset: selectedRange.value,
      weeksBack: selectedRange.weeksBack,
      weeksForward: selectedRange.weeksForward,
      force: true,
    });
  };

  const hasReviewHeaderControls = Boolean(
    (onEvaluate && !isPublishedTarget) ||
    (isPublishedTarget && !publishedPlanUnavailable && !laborPlanSummaryError)
  );

  return (
    <section
      className={`overflow-hidden bg-white ${
        embeddedReviewPanel ? "" : "rounded-xl border border-slate-200"
      }`}
    >
      <div
        onClick={(event) => {
          // Buttons retain their own actions; the rest of the header toggles the review.
          if (event.target.closest("button")) return;
          setSummaryExpanded((current) => !current);
        }}
        className={`relative cursor-pointer transition-colors hover:bg-slate-50 ${hasReviewHeaderControls ? "flex flex-wrap items-center gap-x-5 gap-y-3 pl-6 pr-16 py-4 sm:pl-4 sm:pr-14" : ""} ${
          summaryExpanded ? "border-b border-slate-200" : ""
        }`}
      >
        <button
          type="button"
          className={`flex ${hasReviewHeaderControls ? "min-w-0 flex-[1_1_240px]" : "w-full px-6 py-4"} items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
            embeddedReviewPanel
              ? "rounded-none"
              : summaryExpanded
                ? "rounded-t-xl"
                : "rounded-xl"
          }`}
          aria-expanded={summaryExpanded}
          aria-controls={summaryContentId}
          onClick={() => setSummaryExpanded((current) => !current)}
        >
          <div
            className="min-w-0 flex-1"
          >
            <div className="flex items-start gap-3">
              <LaborModelStepBadge stepNumber={stepNumber} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-900">
                    <span className="sr-only">Step {stepNumber}: </span>
                    {isPublishedTarget
                      ? "Review Published Labor Plan"
                      : "Review Labor Plan"}
                  </h2>
                </div>
                <p className="mt-0.5 text-sm text-slate-500">
                  {isPublishedTarget
                    ? "Review published earned and forecasted labor alongside actual used labor."
                    : "Review sandbox earned and forecasted impact before publishing."}
                </p>
              </div>
            </div>
          </div>
          {!hasReviewHeaderControls && (
          <ExpandMoreIcon
            className={`shrink-0 text-slate-500 transition-transform ${
              summaryExpanded ? "rotate-180" : ""
            }`}
          />
          )}
        </button>
        {onEvaluate && !isPublishedTarget ? (
          <div className="max-w-full flex-[0_1_auto]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {evaluation && !evaluating ? (
                <div className="max-w-full overflow-x-auto">
                  <CompactLaborReviewMetrics
                    scope={`Includes ${selectedStoreIds.length} selected ${selectedStoreIds.length === 1 ? "store" : "stores"} for the review range. Change the scope with Stores and Date range.`}
                    earned={historySummary.target}
                    forecasted={forecastSummary.target}
                    earnedDelta={earnedImpactDelta}
                    forecastedDelta={forecastImpactDelta}
                    laborPercentAvailable={laborPercentAvailable}
                  />
                </div>
              ) : null}
              {resultsAreCurrent ? (
                <Tooltip title="Results up to date" describeChild enterTouchDelay={0}>
                  <span role="status" aria-label="Results up to date" tabIndex={0} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                    <CheckIcon fontSize="small" />
                  </span>
                </Tooltip>
              ) : (
              <button
                type="button"
                aria-label={evaluating ? evaluationButtonLabel : evaluation ? "Recalculate impact" : "Calculate impact"}
                className={`inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-lg px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 ${
                  resultsAreCurrent
                    ? "cursor-default bg-slate-100 text-slate-500"
                    : evaluating
                      ? "cursor-wait bg-violet-300 text-white"
                      : "bg-violet-600 text-white hover:bg-violet-700"
                }`}
                onClick={refreshEvaluation}
                disabled={evaluating || !evaluationKey || resultsAreCurrent}
              >
                {resultsAreCurrent ? (
                  <CheckIcon className="mr-1.5" fontSize="small" />
                ) : (
                  <ScienceOutlinedIcon className="mr-1.5" fontSize="small" />
                )}
                {evaluating
                  ? evaluationButtonLabel
                  : resultsAreCurrent
                    ? "Results up to date"
                  : evaluation
                    ? "Recalculate"
                    : "Calculate impact"}
              </button>
              )}
            </div>
            {!evaluating && evaluationError ? (
              <p
                className="mt-1 max-w-[260px] text-right text-xs text-rose-600 lg:text-left"
                aria-live="polite"
              >
                {evaluationError}
              </p>
            ) : null}
          </div>
        ) : null}
        {hasReviewHeaderControls ? (
          <button
            type="button"
            aria-label={summaryExpanded ? "Collapse labor plan review" : "Expand labor plan review"}
            aria-expanded={summaryExpanded}
            aria-controls={summaryContentId}
            onClick={() => setSummaryExpanded((current) => !current)}
            className="absolute right-6 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 sm:right-4"
          >
            <ExpandMoreIcon className={`transition-transform ${summaryExpanded ? "rotate-180" : ""}`} />
          </button>
        ) : null}
        {isPublishedTarget &&
        !publishedPlanUnavailable &&
        !laborPlanSummaryError ? (
          <div className="max-w-full overflow-x-auto">
            {evaluation ? (
              <>
                <CompactLaborReviewMetrics
                    scope={`Includes ${selectedStoreIds.length} selected ${selectedStoreIds.length === 1 ? "store" : "stores"} for the review range. Change the scope with Stores and Date range.`}
                  earned={historySummary.target}
                  forecasted={forecastSummary.target}
                  laborPercentAvailable={laborPercentAvailable}
                  showImpact={false}
                />
              </>
            ) : (
              <button
                type="button"
                className="inline-flex h-10 cursor-wait items-center rounded-lg bg-slate-100 px-4 text-sm font-semibold text-slate-500"
                disabled
              >
                <RefreshIcon
                  className="mr-1.5 animate-spin"
                  fontSize="small"
                />
                Loading plan…
              </button>
            )}
          </div>
        ) : null}
      </div>

      {summaryExpanded ? (
        <div id={summaryContentId}>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-slate-200 bg-slate-50/50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <div
                className={`inline-flex h-10 shrink-0 rounded-lg border border-slate-200 p-1 ${
                  evaluation ? "bg-white" : "bg-slate-100"
                }`}
                role="group"
                aria-label="Labor plan view"
              >
                {[
                  { value: "overview", label: "Trends" },
                  { value: "comparison", label: "Table" },
                ].map((option) => {
                  const selected = laborPlanView === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`rounded-md px-3 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
                        evaluation
                          ? selected
                            ? "bg-violet-700 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-50"
                          : selected
                            ? "cursor-not-allowed bg-slate-200 text-slate-500"
                            : "cursor-not-allowed text-slate-400"
                      }`}
                      onClick={() => setLaborPlanView(option.value)}
                      aria-pressed={selected}
                      disabled={!evaluation}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {onAdvancedAnalysis ? (
                <button
                  type="button"
                  className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
                  onClick={onAdvancedAnalysis}
                >
                  Advanced analysis
                </button>
              ) : null}
            </div>
            <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
              <CompanyStoreSelector
                stores={stores}
                selectedStoreIds={selectedStoreIds}
                onChange={setSelectedStoreIds}
                title="Stores included"
                description="Filter the metrics, trend, and store impact together."
                menuAlign="right"
              />
              <RightworkSelect
                id="review-labor-plan-date-range"
                label="Range"
                options={REVIEW_RANGE_OPTIONS}
                value={reviewRangePreset}
                onChange={handleReviewRangeChange}
                className="w-[250px]"
                showLabel={false}
              />
            </div>
          </div>
          {publishedPlanUnavailable ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-5 text-center">
          <p className="text-sm font-semibold text-slate-900">
            Saved labor results are available for the live model
          </p>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            This publication’s settings are preserved above, but persisted labor
            results belong to the model that was live for each date. Create a
            sandbox copy to evaluate this version against current operating data.
          </p>
        </div>
      ) : isPublishedTarget && laborPlanSummaryError ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-5 text-center">
          <p className="text-sm font-semibold text-slate-900">
            Published labor plan unavailable
          </p>
          <p className="mt-1 max-w-lg text-sm text-slate-500">
            {laborPlanSummaryError}
          </p>
          {onRetryPublishedPlan ? (
            <button
              type="button"
              className="mt-4 inline-flex h-9 items-center rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              onClick={() => onRetryPublishedPlan(selectedRange)}
            >
              <RefreshIcon className="mr-1.5" fontSize="small" />
              Try again
            </button>
          ) : null}
        </div>
      ) : isPublishedTarget && !evaluation ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-5 text-center">
          <p className="text-sm font-semibold text-slate-900">
            Loading published labor plan
          </p>
          <p className="mt-1 text-sm text-slate-500" aria-live="polite">
            Loading saved earned, forecasted, and actual labor values.
          </p>
        </div>
      ) : evaluating && !evaluation ? (
        <div className="flex min-h-[260px] flex-col items-center justify-center px-5 text-center">
          <ScienceOutlinedIcon className="animate-pulse text-violet-600" />
          <p className="mt-3 text-sm font-semibold text-slate-900">
            Calculating this labor plan
          </p>
          <p className="mt-1 text-sm text-slate-500" aria-live="polite">
            {evaluationStage === "forecast"
                ? "Sandbox earned labor is ready. Calculating forecasted labor."
                : "Calculating sandbox earned labor first."}
          </p>
        </div>
      ) : evaluationError && !evaluation ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-5 text-center">
          <p className="text-sm font-semibold text-slate-900">
            Labor plan review is unavailable
          </p>
          <p className="mt-1 max-w-lg text-sm text-slate-500">
            {evaluationError}
          </p>
          <button
            type="button"
            className="mt-4 inline-flex h-9 items-center rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
            onClick={refreshEvaluation}
          >
            <RefreshIcon className="mr-1.5" fontSize="small" />
            Try again
          </button>
        </div>
      ) : evaluation ? (
        <div className={laborPlanView === "overview" ? "px-4 py-3" : "p-4"}>
          {laborPlanView === "overview" ? (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            <ImpactSummaryCard
              eyebrow={`${laborLabel} earned labor`}
              dateRange={formatDateRange(
                historyEvaluation?.dateStart,
                historyEvaluation?.dateEnd
              )}
              metrics={historySummary.target}
              laborPercentAvailable={laborPercentAvailable}
              value={formatPrimaryMetric(
                historySummary.target,
                primaryMetricKey
              )}
              primaryMetricLabel={getPrimaryMetricLabel(primaryMetricKey)}
              comparisonStatements={[
                !isPublishedTarget
                  ? getLaborHoursPercentComparison(
                      historySummary.target.hours,
                      publishedEarnedMetrics.hours,
                      "the published model"
                    )
                  : null,
                actualCoverage.complete
                  ? getLaborHoursPercentComparison(
                      historySummary.target.hours,
                      historySummary.comparison.hours,
                      "actual used labor"
                    )
                    : null,
              ]}
              comparisonSubject={comparisonSubject}
              compact
            />
            <ImpactSummaryCard
              eyebrow={`${laborLabel} forecasted labor`}
              dateRange={formatDateRange(
                forecastEvaluation?.dateStart,
                forecastEvaluation?.dateEnd
              )}
              metrics={forecastSummary.target}
              laborPercentAvailable={laborPercentAvailable}
              value={formatPrimaryMetric(
                forecastSummary.target,
                primaryMetricKey
              )}
              primaryMetricLabel={getPrimaryMetricLabel(primaryMetricKey)}
              comparisonStatements={
                isPublishedTarget
                  ? null
                  : [
                      getLaborHoursPercentComparison(
                        forecastSummary.target.hours,
                        forecastSummary.comparison.hours,
                        "the published model"
                      ),
                    ]
              }
              comparisonSubject={comparisonSubject}
              compact
            />
              </div>

              <LaborPlanComparisonChart
            seriesRows={chartRows}
            scenarioSeriesRows={isPublishedTarget ? null : scenarioChartRows}
            storeCount={selectedStoreIds.length}
            scenarioLabel={`${laborLabel} labor`}
            description={`${laborLabel} labor compared with actual used and published labor.`}
            versionMarkers={publicationMarkers}
            showForecasted
            consolidatePublishedSeries={!isPublishedTarget}
            legendBelow
            compact
            interactiveLegend
            dense
            visibleSeries={visibleSeries}
            onToggleSeries={toggleSeries}
              />
            </>
          ) : null}

          <StorePlanCards
            rows={storePlanRows}
            refreshing={evaluating}
            visibleSeries={visibleSeries}
            isPublishedTarget={isPublishedTarget}
            view={laborPlanView}
            comparisonMode={comparisonPeriod}
            onComparisonModeChange={setComparisonPeriod}
            historyDateRange={formatDateRange(
              historyEvaluation?.dateStart,
              historyEvaluation?.dateEnd
            )}
            forecastDateRange={formatDateRange(
              forecastEvaluation?.dateStart,
              forecastEvaluation?.dateEnd
            )}
          />
        </div>
          ) : !isPublishedTarget ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-5 text-center">
          <p className="text-sm font-medium text-slate-500">
            Choose a store scope and range, then calculate the impact.
          </p>
          <button
            type="button"
            className="mt-4 inline-flex h-10 items-center rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-violet-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            onClick={refreshEvaluation}
            disabled={!evaluationKey}
          >
            <ScienceOutlinedIcon className="mr-1.5" fontSize="small" />
            Calculate impact
          </button>
        </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default LaborModelPlanSummary;
